import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { FileText, Loader2, CheckCircle, Sparkles } from 'lucide-react';
import { paymentsAPI } from '@/lib/api';

const SignupPage = () => {
  const [searchParams] = useSearchParams();
  const selectedPlan = searchParams.get('plan'); // 'professional' or 'business'
  const selectedBilling = searchParams.get('billing') || 'monthly'; // 'monthly' or 'yearly'
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    company: '',
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  // Plan display info
  const planInfo = {
    professional: { name: 'Professional', monthlyPrice: 29, yearlyPrice: 290 },
    business: { name: 'Business', monthlyPrice: 49, yearlyPrice: 490 }
  };
  const plan = selectedPlan ? planInfo[selectedPlan] : null;
  const displayPrice = plan ? (selectedBilling === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice) : null;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Field-specific validation with clear error messages
    const missingFields = [];
    if (!formData.name?.trim()) missingFields.push('Full Name');
    if (!formData.email?.trim()) missingFields.push('Email');
    if (!formData.password) missingFields.push('Password');
    
    if (missingFields.length > 0) {
      setError(`Please fill in: ${missingFields.join(', ')}`);
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!agreedToTerms) {
      setError('You must agree to the Terms of Service and Privacy Policy');
      return;
    }

    setLoading(true);

    try {
      await signup(formData);
      
      // If user selected a plan, redirect to Stripe checkout
      if (selectedPlan) {
        try {
          const packageId = `${selectedPlan}_${selectedBilling}`;
          const response = await paymentsAPI.createCheckout(packageId);
          window.location.href = response.data.url;
          return;
        } catch (checkoutErr) {
          console.error('Checkout redirect failed:', checkoutErr);
          // Fall through to dashboard if checkout fails
        }
      }
      
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <FileText className="h-8 w-8 text-blue-600" />
            <span className="text-xl font-bold text-slate-900">ProjectStatusReport</span>
          </Link>
        </div>

        {/* Selected Plan Banner */}
        {plan && (
          <div className="mb-4 p-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg shadow-lg">
            <div className="flex items-center gap-3">
              <Sparkles className="h-5 w-5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-medium">
                  {plan.name} Plan Selected
                </p>
                <p className="text-sm text-blue-100">
                  ${displayPrice}/{selectedBilling === 'yearly' ? 'year' : 'month'} 
                  {selectedBilling === 'yearly' && ' (2 months free!)'}
                </p>
              </div>
            </div>
            <p className="text-xs text-blue-200 mt-2">
              Create your account to continue to checkout
            </p>
          </div>
        )}

        <Card>
          <CardHeader className="text-center">
            <CardTitle>{plan ? 'Create Your Account' : 'Start Your Free Trial'}</CardTitle>
            <CardDescription>
              {plan ? 'Then proceed to secure checkout' : '14 days free. No credit card required.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  required
                  data-testid="signup-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  required
                  data-testid="signup-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="At least 6 characters"
                  required
                  data-testid="signup-password-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company">Company (optional)</Label>
                <Input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Your company name"
                  data-testid="signup-company-input"
                />
              </div>

              {/* Terms and Privacy Agreement */}
              <div className="flex items-start gap-2">
                <Checkbox
                  id="terms"
                  checked={agreedToTerms}
                  onCheckedChange={setAgreedToTerms}
                  className="mt-1"
                  data-testid="signup-terms-checkbox"
                />
                <Label htmlFor="terms" className="text-sm text-slate-600 leading-relaxed">
                  I agree to the{' '}
                  <Link to="/terms" target="_blank" className="text-blue-600 underline hover:text-blue-700">
                    Terms of Service
                  </Link>
                  {' '}and{' '}
                  <Link to="/privacy" target="_blank" className="text-blue-600 underline hover:text-blue-700">
                    Privacy Policy
                  </Link>
                </Label>
              </div>

              <Button type="submit" className="w-full" disabled={loading || !agreedToTerms} data-testid="signup-submit-btn">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {plan ? 'Create Account & Continue to Checkout' : 'Create Account'}
              </Button>
            </form>

            <div className="mt-6 space-y-2">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span>14-day free trial</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span>Full feature access</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span>No credit card required</span>
              </div>
            </div>

            <div className="mt-6 text-center text-sm">
              <span className="text-slate-600">Already have an account? </span>
              <Link to="/login" className="text-blue-600 hover:underline" data-testid="login-link">
                Sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SignupPage;
